import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { FiCheck, FiHelpCircle } from "react-icons/fi";

const Pricing = () => {
  const { t, language } = useLanguage();
  const [isYearly, setIsYearly] = useState(true);

  const toggleBilling = () => {
    setIsYearly(!isYearly);
  };

  const plans = [
    {
      name: isYearly ? t("yearly") : t("monthly"),
      description: isYearly ? t("yearlyDescription") : t("monthlyDescription"),
      price: isYearly ? 799.99 : 79.99,
      period: isYearly ? t("year") : t("month"),
      features: [
        t("restaurantMobileAdmin"),
        t("customerMobileOrdering"),
        t("realTimeNotifications"),
        t("orderTrackingSystem"),
        t("productManagement"),
        t("categoryManagement"),
        t("discountCodes"),
        t("lightDarkTheme"),
        t("multiLanguageSupport"),
        t("unlimitedOrders"),
        t("unlimitedMenuItems"),
        t("freeTechnicalSupport"),
      ],
      cta: t("getStarted"),
      ctaLink: "/contact",
      highlighted: true,
      promotion: t("firstMonthFree"),
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="py-10 bg-gradient-to-br from-gray-50 to-white">
        <div className="container-custom text-center max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            {t("pricingTitle")}
          </h1>
          <p className="text-xl text-gray-700 mb-6">
            {t("pricingDescription")}
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center mb-4">
            <span
              className={`text-sm font-medium ${
                !isYearly ? "text-gray-900" : "text-gray-500"
              }`}
            >
              {t("monthly")}
            </span>
            <label
              htmlFor="billing-toggle"
              className="mx-4 relative inline-flex items-center cursor-pointer"
            >
              <input
                type="checkbox"
                id="billing-toggle"
                className="sr-only"
                checked={isYearly}
                onChange={toggleBilling}
              />
              <div
                className={`w-14 h-7 bg-gray-200 rounded-full p-1 transition ${
                  isYearly ? "bg-orange-500" : ""
                }`}
              >
                <div
                  className={`dot w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-300 ${
                    isYearly ? "translate-x-7" : ""
                  }`}
                ></div>
              </div>
            </label>
            <span
              className={`text-sm font-medium ${
                isYearly ? "text-gray-900" : "text-gray-500"
              }`}
            >
              {t("yearly")}{" "}
              <span className="text-orange-500 ml-1">{t("save20Percent")}</span>
            </span>
          </div>
        </div>
      </section>

      {/* Pricing Cards Section */}
      <section className="pt-2 pb-16 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Left Side: Free Trial */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="price-card bg-blue-50 border border-blue-100 px-8 py-8 h-full flex flex-col"
            >
              <div className="mb-6">
                <div className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full mb-3 inline-block">
                  {t("recommended")}
                </div>
                <h3 className="text-2xl font-bold mb-3 text-blue-700">
                  {t("freeTrialTitle")}
                </h3>
                <p className="text-gray-700 mb-6">
                  {t("freeTrialDescription")}
                </p>

                <div className="mb-4">
                  <span className="text-4xl font-bold text-blue-700">$0</span>
                  <span className="text-gray-500 ml-1">/ 30 {t("days")}</span>
                </div>

                <div className="p-3 bg-blue-100 rounded-lg mb-6">
                  <p className="text-blue-800 text-sm font-medium">
                    {t("perfectFor")}
                  </p>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <FiCheck className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{t("fullAccess")}</span>
                </li>
                <li className="flex items-start">
                  <FiCheck className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">
                    {t("restaurantMobileAdmin")}
                  </span>
                </li>
                <li className="flex items-start">
                  <FiCheck className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">
                    {t("customerMobileOrdering")}
                  </span>
                </li>
                <li className="flex items-start">
                  <FiCheck className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">
                    {t("orderTrackingSystem")}
                  </span>
                </li>
                <li className="flex items-start">
                  <FiCheck className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{t("noCommitment")}</span>
                </li>
                <li className="flex items-start">
                  <FiCheck className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{t("noCreditCard")}</span>
                </li>
                <li className="flex items-start">
                  <FiCheck className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{t("cancelAnytime")}</span>
                </li>
              </ul>

              <div className="mt-auto">
                <Link
                  to="/contact"
                  className="w-full btn btn-secondary bg-blue-600 hover:bg-blue-700 hover:text-white text-white border-0"
                >
                  {t("startFreeTrial")}
                </Link>
                <p className="text-sm text-center mt-3 text-gray-600">
                  {t("noPaymentRequired")}
                </p>
              </div>
            </motion.div>

            {/* Right Side: Paid Plan */}
            <div>
              {plans.map((plan, index) => (
                <motion.div
                  key={plan.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="price-card price-card-highlight px-8 py-8 h-full"
                >
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold mb-2">
                      {plan.name} {t("plan")}
                    </h3>
                    <p className="text-gray-600 mb-3">{plan.description}</p>

                    {/* Promotion Badge */}
                    {plan.promotion && (
                      <div className="inline-block bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full mb-3">
                        {plan.promotion}
                      </div>
                    )}

                    <div className="mb-3">
                      <span className="text-4xl font-bold">${plan.price}</span>
                      <span className="text-gray-500 ml-1">/{plan.period}</span>
                    </div>
                    {isYearly && (
                      <p className="text-sm text-green-600 font-medium">
                        ${(79.99 * 12).toFixed(2)} {t("billedMonthly")} -{" "}
                        {t("save")} ${(79.99 * 12 - 799.99).toFixed(2)}
                      </p>
                    )}
                  </div>

                  <ul className="space-y-2 mb-6">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <FiCheck className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={plan.ctaLink}
                    className="w-full btn btn-primary text-center"
                  >
                    {plan.cta}
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">
              {t("frequentlyAskedQuestions")}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t("faqDescription")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-start mb-2">
                <FiHelpCircle className="h-6 w-6 text-orange-500 mr-2 flex-shrink-0 mt-0.5" />
                <h3 className="text-lg font-bold">
                  {t("whatsIncludedQuestion")}
                </h3>
              </div>
              <p className="text-gray-700 ml-8">{t("whatsIncludedAnswer")}</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-start mb-2">
                <FiHelpCircle className="h-6 w-6 text-orange-500 mr-2 flex-shrink-0 mt-0.5" />
                <h3 className="text-lg font-bold">{t("demoQuestion")}</h3>
              </div>
              <p className="text-gray-700 ml-8">
                {t("demoAnswer")}{" "}
                <a
                  href="https://foodapp.solylock.dev"
                  className="text-orange-500 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  foodapp.solylock.dev
                </a>
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-start mb-2">
                <FiHelpCircle className="h-6 w-6 text-orange-500 mr-2 flex-shrink-0 mt-0.5" />
                <h3 className="text-lg font-bold">
                  {t("paymentMethodsQuestion")}
                </h3>
              </div>
              <p className="text-gray-700 ml-8">{t("paymentMethodsAnswer")}</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-start mb-2">
                <FiHelpCircle className="h-6 w-6 text-orange-500 mr-2 flex-shrink-0 mt-0.5" />
                <h3 className="text-lg font-bold">
                  {t("cancelSubscriptionQuestion")}
                </h3>
              </div>
              <p className="text-gray-700 ml-8">
                {t("cancelSubscriptionAnswer")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="rounded-2xl bg-gradient-to-r from-orange-500 to-red-600 p-8 md:p-12 shadow-md text-white text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              {t("readyToRevolutionize")}
            </h2>
            <p className="text-lg md:text-xl mb-8 max-w-2xl mx-auto">
              {t("joinRestaurants")}
            </p>
            <Link
              to="/contact"
              className="bg-white text-orange-600 hover:bg-gray-100 py-3 px-8 rounded-lg font-medium shadow-sm inline-block"
            >
              {t("contactSales")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
